/**
 * Functions to interact with the backend api.
 */
import { fetch } from '../fetch';
import { clearAccessToken } from './accessToken';

/**
 * Retrieve the currently logged in user from `/auth/me`.
 *
 * @returns the parsed user payload, or `null` when not authenticated. The
 * payload shape is not read here, so it stays opaque (`unknown`).
 */
export function me(): Promise<unknown> {
  return fetch('/auth/me', {
    headers: {
      Accept: 'application/json',
    },
  }).then(response => {
    if (response.ok) {
      // DOM lib types `Response.json()` as `Promise<any>`; keep it opaque.
      return response.json() as Promise<unknown>;
    } else {
      return Promise.resolve(null);
    }
  });
}

/**
 * Register a new user
 *
 * @param firstname
 * @param lastname
 * @param email
 * @param type One of 'STUDENT', 'PERSONAL', 'COMPANY', 'OTHER'
 * @param affiliation
 * @param token The registration token to prove the user identity to
 * the backend. We got this from the backend through a query parameter after
 * being redirect to /register after logging in to the identity provider.
 */
export function register(
  firstname: string,
  lastname: string,
  email: string,
  type: string,
  affiliation: string,
  token: string
): Promise<unknown> {
  const body = { firstname, lastname, email, type, affiliation, token };
  return fetch('/auth/register', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).then(response => {
    if (response.ok) {
      return me();
    } else {
      return Promise.reject('Unable to register');
    }
  });
}

/**
 * Logout
 *
 * Tells the backend to revoke the refresh token + clear its cookie and tear down
 * the session, then drops the in-memory access token so no stale Bearer lingers
 * for the rest of the page's life. The access token is cleared regardless of the
 * response: once the user has asked to log out, the SPA must not keep presenting
 * a Bearer, and a failed `/auth/logout` is surfaced to the caller as before.
 */
export function logout(): Promise<string> {
  return fetch('/auth/logout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(response => {
    clearAccessToken();
    if (response.ok) {
      return 'ok';
    } else {
      return Promise.reject('Logout failed');
    }
  });
}

/**
 * Update account details
 *
 * @param updates
 */
export function update(updates: unknown): Promise<unknown> {
  return fetch('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(updates),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).then(response => {
    if (response.ok) {
      return response.json() as Promise<unknown>;
    } else {
      return Promise.reject('Update failed');
    }
  });
}
